import {
  ReportFormat,
  ReportFormatDisplayName,
  ReportFormatEnum,
} from "pmt-types/src/enums/ReportFormat.ts";
import { ReportType } from "pmt-types/src/enums/ReportType.ts";
import { Project } from "pmt-types/src/models/Project.ts";
import { Report } from "pmt-types/src/models/Report.ts";
import { Source } from "pmt-types/src/models/Source.ts";
import {
  BidDeductReportParams,
  BidDeductReportParamsSchema,
} from "pmt-types/src/models/reports/BidDeductReportParams.ts";
import { EnrollmentStatusBidCreditReportParams } from "pmt-types/src/models/reports/EnrollmentStatusBidCreditReportParams.ts";
import { useState } from "react";
import Checkbox from "../../components/form/checkbox.tsx";
import EntityAsyncSelect from "../../components/form/entityAsyncSelect.tsx";
import EntitySelect from "../../components/form/entitySelect.tsx";
import EnumSelect from "../../components/form/enumSelect.tsx";
import PmtForm from "../../components/form/pmtForm.tsx";
import FormRoot from "../../components/form/style/formRoot.tsx";
import FormSection from "../../components/form/style/formSection.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import { useMutationAddReport } from "../../hooks/mutations/useMutationAddReport.ts";
import { useMutationSearchSources } from "../../hooks/mutations/useMutationSearchSources.ts";
import { useQueryGetProjectsForSourceId } from "../../hooks/queries/useQueryGetProjectsForSourceId.ts";
import { Maybe } from "../../utils/types/maybe.ts";

type Props = {
  onAddReport: (report: Report) => void;
};

export default function BidDeductReportForm({ onAddReport }: Props) {
  const [selectedSourceId, setSelectedSourceId] = useState<Maybe<string>>(null);

  const { mutateAsync: searchSources } = useMutationSearchSources();
  const { data: projects } = useQueryGetProjectsForSourceId(selectedSourceId);

  const { mutate, isLoading: isSubmitting } = useMutationAddReport();

  const buildReportFromParams = (
    params: EnrollmentStatusBidCreditReportParams,
  ): Report => ({
    id: "new",
    name: `Bid Deduct for ${params.projectName ?? params.sourceName}`,
    reportType: ReportType.enum.BidDeduct,
    reportFormat: params.reportFormat,
    parameters: { ...params },
  });

  return (
    <PmtForm<BidDeductReportParams>
      schema={BidDeductReportParamsSchema}
      defaultValues={{ reportFormat: ReportFormat.enum.Pdf }}
      isSubmitting={isSubmitting}
      formStyle={"no-submit"}
      saveLabel={"Build Report"}
      onSave={(values) => {
        mutate(buildReportFromParams(values), {
          onSuccess: (report: Report) => {
            onAddReport(report);
          },
          onError: (error) => {
            console.error("Failed to add report", error);
          },
        });
      }}
      renderForm={(form, submit) => {
        form.register("sourceId");
        form.register("sourceName");
        form.register("projectId");
        form.register("projectName");

        return (
          <FormRoot>
            <FormSection>
              <EntityAsyncSelect<Pick<Source, "id" | "name">>
                name="sourceId"
                label="Source"
                placeholder="Search by source name..."
                renderLabel={(value) => {
                  if (value == null) {
                    return "";
                  }

                  return `${value.name}`;
                }}
                queryFn={async (filter: string) => {
                  return await searchSources(filter);
                }}
                onChange={async (value) => {
                  if (value?.id == null) {
                    setSelectedSourceId(null);
                    return;
                  }

                  setSelectedSourceId(value.id);
                  form.setValue("sourceId", value.id);
                  form.setValue("sourceName", value.name);
                  await form.trigger();
                }}
              />
              <EntitySelect<Pick<Project, "id" | "name">>
                name="projectId"
                label="Project (optional)"
                data={projects ?? []}
                formatOption={(value) => {
                  if (value == null) {
                    return "";
                  }

                  return `${value.name}`;
                }}
                onChange={async (value) => {
                  if (value?.id == null) {
                    form.setValue("projectId", undefined);
                    form.setValue("projectName", undefined);
                    await form.trigger();
                    return;
                  }

                  form.setValue("projectId", value.id);
                  form.setValue("projectName", value.name);
                  await form.trigger();
                }}
              />
              <Checkbox name="requireSignature" label="Require Signature?" />
              <EnumSelect
                name="reportFormat"
                label="Format"
                data={ReportFormat.options}
                formatOption={(option) =>
                  ReportFormatDisplayName[option as ReportFormatEnum]
                }
              />
              <div className="flex flex-col w-48 justify-end">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={submit}
                    disabled={isSubmitting}
                    className="h-10 mb-1.5 rounded-md bg-sky-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-600"
                  >
                    Build Report
                  </button>
                  {isSubmitting && (
                    <div className="flex items-center justify-center pb-1 pl-1">
                      <LoadingSpinner />
                    </div>
                  )}
                </div>
              </div>
            </FormSection>
          </FormRoot>
        );
      }}
    />
  );
}
